"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Ruler } from "lucide-react"
import dynamic from "next/dynamic"
import { ErrorLogger } from "@/lib/error-logger"

console.log("[v0 PAGE] Starting to load page component...")

const RoomViewer = dynamic(
  () => {
    console.log("[v0 PAGE] Dynamic import: Starting to load RoomViewer...")
    return import("@/components/room-viewer")
      .then((mod) => {
        console.log("[v0 PAGE] Dynamic import: RoomViewer loaded successfully!")
        return mod
      })
      .catch((error) => {
        console.error("[v0 PAGE] Dynamic import: Failed to load RoomViewer!", error)
        ErrorLogger.log("ERROR", "Failed to load RoomViewer component", { error: error.message, stack: error.stack })
        throw error
      })
  },
  {
    ssr: false,
    loading: () => {
      console.log("[v0 PAGE] Showing loading state for RoomViewer...")
      return (
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-4">
            <Card>
              <CardContent className="p-0">
                <div className="h-[600px] flex items-center justify-center bg-gradient-to-b from-slate-900 to-slate-800">
                  <p className="text-muted-foreground">جاري تحميل العارض ثلاثي الأبعاد...</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },
  },
)

export default function HomePage() {
  console.log("[v0 PAGE] HomePage component rendering...")

  const [showRoom, setShowRoom] = useState(false)
  const [dimensions, setDimensions] = useState({
    length: 5,
    width: 4,
    height: 3,
  })

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    console.log("[v0 PAGE] useEffect: Component mounted on client")
    ErrorLogger.log("INFO", "HomePage mounted", {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      dimensions,
    })
    setMounted(true)
  }, [])

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error("[v0 PAGE] Window error caught:", event.error)
      ErrorLogger.log("ERROR", "Window error event", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.message,
        stack: event.error?.stack,
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("[v0 PAGE] Unhandled promise rejection:", event.reason)
      ErrorLogger.log("ERROR", "Unhandled promise rejection", {
        reason: event.reason?.message || event.reason,
        stack: event.reason?.stack,
      })
    }

    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleUnhandledRejection)

    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleUnhandledRejection)
    }
  }, [])

  if (showRoom && mounted) {
    console.log("[v0 PAGE] Rendering RoomViewer with dimensions:", dimensions)
    ErrorLogger.log("INFO", "Showing RoomViewer", { dimensions })

    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-4">
          <RoomViewer
            dimensions={dimensions}
            onBack={() => {
              console.log("[v0 PAGE] User clicked back button")
              ErrorLogger.log("INFO", "User navigated back from RoomViewer")
              setShowRoom(false)
            }}
          />
        </div>
      </div>
    )
  }

  console.log("[v0 PAGE] Rendering dimensions form, mounted:", mounted)

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">عارض الغرف ثلاثي الأبعاد</CardTitle>
          <p className="text-sm text-muted-foreground text-center">أدخل مقاسات الغرفة لإنشاء نموذج ثلاثي الأبعاد</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="length" className="flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              الطول (متر)
            </Label>
            <Input
              id="length"
              type="number"
              min="1"
              max="20"
              step="0.5"
              value={dimensions.length}
              onChange={(e) => setDimensions({ ...dimensions, length: Number.parseFloat(e.target.value) || 1 })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="width" className="flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              العرض (متر)
            </Label>
            <Input
              id="width"
              type="number"
              min="1"
              max="20"
              step="0.5"
              value={dimensions.width}
              onChange={(e) => setDimensions({ ...dimensions, width: Number.parseFloat(e.target.value) || 1 })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="height" className="flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              الارتفاع (متر)
            </Label>
            <Input
              id="height"
              type="number"
              min="2"
              max="6"
              step="0.5"
              value={dimensions.height}
              onChange={(e) => setDimensions({ ...dimensions, height: Number.parseFloat(e.target.value) || 2 })}
            />
          </div>

          <Button
            onClick={() => {
              console.log("[v0 PAGE] User clicked create room button")
              ErrorLogger.log("INFO", "User creating room", { dimensions })
              setShowRoom(true)
            }}
            className="w-full"
            disabled={!mounted}
          >
            {mounted ? "إنشاء الغرفة" : "جاري التحميل..."}
          </Button>

          <Button
            onClick={() => {
              console.log("[v0 PAGE] Downloading error logs...")
              ErrorLogger.downloadLogs()
            }}
            variant="outline"
            className="w-full text-xs"
          >
            تحميل سجل الأخطاء
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
